import Box from "@/elements/Box";
import Input from "@/elements/Input";
import Typography from "@/elements/Typography";
import { FormHelperText, Grid, Paper, Slide } from "@mui/material";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RichiestaPreventivoCreateDto } from "@/api";

interface Step1DatiAziendaProps {
  activeStep: number;
  slideDirection: "left" | "right";
  register: UseFormRegister<RichiestaPreventivoCreateDto>;
  errors: FieldErrors<RichiestaPreventivoCreateDto>;
}

export function Step1DatiAzienda({
  activeStep,
  slideDirection,
  register,
  errors,
}: Step1DatiAziendaProps) {
  const { t } = useTranslation();

  return (
    <Slide
      direction={slideDirection}
      in={activeStep === 1}
      timeout={500}
      unmountOnExit
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          bgcolor: "white",
          position: activeStep === 1 ? "relative" : "absolute",
          width: "100%",
          top: 0,
        }}
      >
        <Box>
          <Typography variant="h5" gutterBottom mb={3} sx={{ color: "#333" }}>
            {t("richiestaPreventivo.step1.title")}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box mb={1} ml={0.5}>
                <Typography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                >
                  {t("richiestaPreventivo.step1.nomeAzienda")} *
                </Typography>
              </Box>
              <Input
                fullWidth
                placeholder={t(
                  "richiestaPreventivo.step1.placeholder.nomeAzienda",
                )}
                {...register("nomeAzienda", {
                  required: t("richiestaPreventivo.step1.required"),
                })}
                error={!!errors.nomeAzienda?.message}
              />
              {errors.nomeAzienda?.message && (
                <FormHelperText error>
                  {errors.nomeAzienda?.message}
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Box mb={1} ml={0.5}>
                <Typography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                >
                  {t("richiestaPreventivo.step1.nomeRichiedente")} *
                </Typography>
              </Box>
              <Input
                fullWidth
                placeholder={t(
                  "richiestaPreventivo.step1.placeholder.nomeRichiedente",
                )}
                {...register("nomeRichiedente", {
                  required: t("richiestaPreventivo.step1.required"),
                })}
                error={!!errors.nomeRichiedente?.message}
              />
              {errors.nomeRichiedente?.message && (
                <FormHelperText error>
                  {errors.nomeRichiedente?.message}
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Box mb={1} ml={0.5}>
                <Typography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                >
                  {t("richiestaPreventivo.step1.posizione")} *
                </Typography>
              </Box>
              <Input
                fullWidth
                placeholder={t(
                  "richiestaPreventivo.step1.placeholder.posizione",
                )}
                {...register("posizione", {
                  required: t("richiestaPreventivo.step1.required"),
                })}
                error={!!errors.posizione?.message}
              />
              {errors.posizione?.message && (
                <FormHelperText error>
                  {errors.posizione?.message}
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Box mb={1} ml={0.5}>
                <Typography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                >
                  {t("richiestaPreventivo.step1.email")} *
                </Typography>
              </Box>
              <Input
                fullWidth
                placeholder={t("richiestaPreventivo.step1.placeholder.email")}
                type="email"
                {...register("email", {
                  required: t("richiestaPreventivo.step1.required"),
                  setValueAs: (value) => value?.trim(),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t("richiestaPreventivo.step1.emailInvalid"),
                  },
                })}
                error={!!errors.email?.message}
              />
              {errors.email?.message && (
                <FormHelperText error>{errors.email?.message}</FormHelperText>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Box mb={1} ml={0.5}>
                <Typography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                >
                  {t("richiestaPreventivo.step1.numeroTelefono")} *
                </Typography>
              </Box>
              <Input
                fullWidth
                placeholder={t(
                  "richiestaPreventivo.step1.placeholder.numeroTelefono",
                )}
                type="tel"
                {...register("numeroTelefono", {
                  required: t("richiestaPreventivo.step1.required"),
                })}
                error={!!errors.numeroTelefono?.message}
              />
              {errors.numeroTelefono?.message && (
                <FormHelperText error>
                  {errors.numeroTelefono?.message}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Slide>
  );
}
